import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useQuote } from '../context/QuoteContext';
import { Button } from '../components/ui/button';
import ProductSearchButton from '../components/ProductSearchButton';
import { Checkbox } from '../components/ui/checkbox';
import { Package, ChevronRight, ChevronDown, ChevronUp, SlidersHorizontal, Plus, X, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import SEO, { generateBreadcrumbSchema, generateItemListSchema } from '../components/SEO';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { productImage } from '../utils/imageUtils';
import {
  FILTER_GROUPS,
  buildFilterOptions,
  productMatchesFilters,
  activeFiltersToText,
} from '../utils/filterNormalization';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoryPage = () => {
  const { categorySlug } = useParams();
  const { t, language } = useLanguage();
  const { addToQuote } = useQuote();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVariants, setExpandedVariants] = useState({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // Filters
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedPressureClasses, setSelectedPressureClasses] = useState([]);
  
  // Filter panel expanded sections
  const [expandedFilters, setExpandedFilters] = useState({
    dimension: false,
    material: false,
    pressureClass: false
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories/tree`)
        ]);
        
        setCategories(categoriesRes.data);
        
        // Find the category by slug
        const foundCategory = categoriesRes.data.find(cat => 
          cat.slug === categorySlug || 
          cat.id === categorySlug
        );
        
        if (!foundCategory) {
          navigate('/produkter');
          return;
        }
        
        setCategory(foundCategory);
        
        // Filter products for this category
        const categoryProducts = productsRes.data.filter(p => 
          p.main_category_id === foundCategory.id || 
          p.main_category_id === foundCategory.slug ||
          p.category === foundCategory.id ||
          p.category === foundCategory.slug
        );
        
        setProducts(categoryProducts);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categorySlug, navigate]);

  const toggleVariants = (productId) => {
    setExpandedVariants(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };
  
  const toggleFilter = (type, value) => {
    const setters = {
      dimension: setSelectedDimensions,
      material: setSelectedMaterials,
      pressureClass: setSelectedPressureClasses
    };
    const current = {
      dimension: selectedDimensions,
      material: selectedMaterials,
      pressureClass: selectedPressureClasses
    };
    
    const setter = setters[type];
    const currentValues = current[type];
    
    if (currentValues.includes(value)) {
      setter(currentValues.filter(v => v !== value));
    } else {
      setter([...currentValues, value]);
    }
  };
  
  const clearFilters = () => {
    setSelectedDimensions([]);
    setSelectedMaterials([]);
    setSelectedPressureClasses([]);
  };
  
  // Smart filter normalization — auto-shows only filters relevant for THIS category's products
  const activeFilters = {
    dimension: selectedDimensions,
    material: selectedMaterials,
    pressureClass: selectedPressureClasses,
  };
  const filterOptions = buildFilterOptions(products, activeFilters);
  const dimensions = filterOptions.dimension || [];
  const materials = filterOptions.material || [];
  const pressureClasses = filterOptions.pressureClass || [];
  
  // Filter products using normalized matching
  const filteredProducts = products.filter((product) => productMatchesFilters(product, activeFilters));
  
  const hasActiveFilters = selectedDimensions.length > 0 || selectedMaterials.length > 0 || selectedPressureClasses.length > 0;

  if (loading) {
    return (
      <div className="bg-[#FAFAF8] min-h-screen pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-stone-200 rounded w-1/3" />
            <div className="h-4 bg-stone-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return null;
  }

  const categoryName = language === 'sv' ? category.name_sv : category.name_en;
  const categoryDesc = language === 'sv' ? category.description_sv : category.description_en;

  // Detect if any filter is active → noindex this filtered view
  return (
    <div data-testid="category-page" className="bg-[#FAFAF8] min-h-screen pt-24">
      <SEO 
        title={`${categoryName} | VVS-produkter`}
        description={categoryDesc || `Utforska ${categoryName} från Flowingo AB. Högkvalitativa VVS-produkter för professionella installatörer och grossister.`}
        language={language}
        canonical={`https://flowingo.se/produkter/kategori/${category.slug}`}
        noindex={hasActiveFilters}
        schema={[
          generateBreadcrumbSchema([
            { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se' },
            { name: language === 'sv' ? 'Produkter' : 'Products', url: 'https://flowingo.se/produkter' },
            { name: categoryName, url: `https://flowingo.se/produkter/kategori/${category.slug}` },
          ]),
          generateItemListSchema(filteredProducts, language, `https://flowingo.se/produkter/kategori/${category.slug}`),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': `https://flowingo.se/produkter/kategori/${category.slug}`,
            name: categoryName,
            description: categoryDesc || `${categoryName} från Flowingo AB`,
            isPartOf: { '@id': 'https://flowingo.se/#website' },
          }
        ]}
      />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-4">
          <Link to="/" className="hover:text-stone-900">{t('nav.home')}</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/produkter" className="hover:text-stone-900">{t('nav.products')}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">{categoryName}</span>
        </nav>
        
        <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-4">
          {categoryName}
        </h1>
        
        {categoryDesc && (
          <p className="text-stone-600 mb-6">
            {categoryDesc}
          </p>
        )}

        {/* Other categories - Show subcategories if current category has children, otherwise show main categories */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Link to="/produkter">
            <button className="px-4 py-2 text-sm border bg-white text-stone-700 border-stone-300 hover:border-stone-400 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {language === 'sv' ? 'Produkter' : 'Products'}
            </button>
          </Link>
          
          {category.children && category.children.length > 0 ? (
            // Show subcategories
            category.children.map(subCat => (
              <Link key={subCat.id} to={`/produkter/kategori/${category.slug}/${subCat.slug}`}>
                <button className="px-4 py-2 text-sm border transition-colors bg-white text-stone-700 border-stone-300 hover:border-stone-400">
                  {language === 'sv' ? subCat.name_sv : subCat.name_en}
                </button>
              </Link>
            ))
          ) : (
            // Show main categories
            categories.map(cat => (
              <Link key={cat.id} to={`/produkter/kategori/${cat.slug}`}>
                <button
                  className={`px-4 py-2 text-sm border transition-colors ${
                    cat.id === category.id
                      ? 'bg-stone-900 text-white border-stone-900'
                      : 'bg-white text-stone-700 border-stone-300 hover:border-stone-400'
                  }`}
                >
                  {language === 'sv' ? cat.name_sv : cat.name_en}
                </button>
              </Link>
            ))
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Filter Bar */}
        <div className="flex items-center justify-between gap-4 mb-6 pb-4 border-b border-stone-200">
          <div className="flex items-center gap-4 flex-wrap">
            <FilterDropdown
              label={language === 'sv' ? FILTER_GROUPS.dimension.label_sv : FILTER_GROUPS.dimension.label_en}
              options={dimensions.map(d => ({ value: d.value, label: d.value, count: d.count }))}
              selected={selectedDimensions}
              onToggle={(val) => toggleFilter('dimension', val)}
            />
            <FilterDropdown
              label={language === 'sv' ? FILTER_GROUPS.material.label_sv : FILTER_GROUPS.material.label_en}
              options={materials.map(m => ({ value: m.value, label: m.value, count: m.count }))}
              selected={selectedMaterials}
              onToggle={(val) => toggleFilter('material', val)}
            />
            <FilterDropdown
              label={language === 'sv' ? FILTER_GROUPS.pressureClass.label_sv : FILTER_GROUPS.pressureClass.label_en}
              options={pressureClasses.map(p => ({ value: p.value, label: p.value, count: p.count }))}
              selected={selectedPressureClasses}
              onToggle={(val) => toggleFilter('pressureClass', val)}
            />
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-[#321a27] hover:underline text-sm flex items-center gap-1">
                {language === 'sv' ? 'Rensa filter' : 'Clear filters'}
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <ProductSearchButton language={language} />
            <Button
              variant="outline"
              onClick={() => setShowFilterPanel(true)}
              className="border-stone-300 text-stone-700 hover:bg-stone-100 rounded-none flex items-center gap-2"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {language === 'sv' ? 'Se alla filter' : 'See all filters'}
            </Button>
          </div>
        </div>

        {/* Results count */}
        <div className="mb-6 text-stone-600 text-sm">
          {filteredProducts.length} {language === 'sv' ? 'produkter' : 'products'}
        </div>

        {/* Products List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white h-48 animate-pulse border border-stone-200" />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 bg-white border border-stone-200">
            <Package className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-600">{t('products.noProducts')}</p>
            <Link to="/produkter" className="mt-4 inline-block">
              <Button variant="outline" className="border-stone-300 text-stone-700 rounded-none">
                {language === 'sv' ? 'Alla produkter' : 'All products'}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts.map((product) => (
              <ProductCard 
                key={product.id}
                product={product}
                language={language}
                t={t}
                isExpanded={expandedVariants[product.id]}
                onToggleVariants={() => toggleVariants(product.id)}
                categories={categories}
                addToQuote={addToQuote}
              />
            ))}
          </div>
        )}

        {/* Category buying guide (SEO text) */}
        {language === 'sv' && category?.seo_text_sv && (
          <section
            className="mt-16 max-w-3xl border-t border-stone-200 pt-10 prose-headings:text-stone-900"
            data-testid="category-seo-text"
          >
            <div
              className="text-stone-600 leading-relaxed [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-stone-900 [&>h3]:mb-3 [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-4 [&>ul>li]:mb-1"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(category.seo_text_sv) }}
            />
          </section>
        )}
      </div>
      
      {/* Filter Panel (slide-in from right) */}
      {showFilterPanel && (
        <FilterPanel
          language={language}
          dimensions={dimensions}
          materials={materials}
          pressureClasses={pressureClasses}
          selectedDimensions={selectedDimensions}
          selectedMaterials={selectedMaterials}
          selectedPressureClasses={selectedPressureClasses}
          expandedFilters={expandedFilters}
          setExpandedFilters={setExpandedFilters}
          toggleFilter={toggleFilter}
          clearFilters={clearFilters}
          onClose={() => setShowFilterPanel(false)}
          resultCount={filteredProducts.length}
        />
      )}
    </div>
  );
};

// Product Card - Reuse same component as ProductsPage
const ProductCard = ({ product, language, t, isExpanded, onToggleVariants, categories, addToQuote }) => {
  const { useQuote } = require('../context/QuoteContext');
  const productName = language === 'sv' ? product.name_sv : product.name_en;
  const productDescription = language === 'sv' ? product.description_sv : product.description_en;
  const variantCount = product.variants?.length || 0;
  
  const mainCatId = product.main_category_id || product.category;
  const subCatId = product.sub_category_id;
  
  let mainCategoryName = '';
  let subCategoryName = '';
  
  for (const cat of categories) {
    if (cat.id === mainCatId || cat.slug === mainCatId) {
      mainCategoryName = language === 'sv' ? cat.name_sv : cat.name_en;
      if (subCatId) {
        for (const sub of cat.children || []) {
          if (sub.id === subCatId || sub.slug === subCatId) {
            subCategoryName = language === 'sv' ? sub.name_sv : sub.name_en;
            break;
          }
        }
      }
      break;
    }
  }
  
  const categoryDisplay = subCategoryName 
    ? `${mainCategoryName} / ${subCategoryName}` 
    : mainCategoryName || mainCatId;

  return (
    <div className="bg-white border border-stone-200">
      <div className="p-4 flex gap-4">
        <div className="w-36 h-36 bg-white flex-shrink-0 overflow-hidden flex items-center justify-center">
          {product.image_url ? (
            <img 
              src={productImage(product.image_url)} 
              alt={productName} 
              width="144" height="144"
              className="w-full h-full object-contain" 
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-400">
              <Package className="w-12 h-12" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
            {categoryDisplay}
          </div>
          <Link 
            to={`/produkter/${product.slug || product.id}`}
            className="text-lg font-semibold text-stone-900 hover:text-[#321a27] block mb-1"
          >
            {productName}
          </Link>
          <p className="text-stone-600 text-sm line-clamp-2 mb-3" dangerouslySetInnerHTML={{ __html: productDescription || '' }} />
          
          {variantCount > 0 && (
            <button
              onClick={onToggleVariants}
              className="px-4 py-2 text-sm border border-[#321a27] text-[#321a27] hover:bg-[#f5f0f2] flex items-center gap-2"
            >
              {isExpanded ? (
                <>
                  {language === 'sv' ? 'Dölj varianter' : 'Hide variants'} ({variantCount})
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  {language === 'sv' ? 'Visa varianter' : 'Show variants'} ({variantCount})
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {isExpanded && product.variants?.length > 0 && (
        <div className="border-t border-stone-200">
          <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
            <h4 className="font-semibold text-stone-900">
              {language === 'sv' ? 'Varianter' : 'Variants'}
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#321a27] text-white">
                <tr>
                  {(product.variant_columns || Object.keys(product.variants[0] || {})).map((col) => (
                    <th key={col} className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">
                      {col.replace(/_/g, ' ')}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-sm font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {product.variants.map((variant, idx) => {
                  const artikelnummer = variant.Artikelnummer || variant.artikelnummer || '';
                  
                  return (
                    <tr 
                      key={idx} 
                      className={`border-b border-stone-200 hover:bg-[#f5f0f2] ${idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}
                    >
                      {(product.variant_columns || Object.keys(product.variants[0] || {})).map((col) => {
                        const isArtikelnummer = col.toLowerCase() === 'artikelnummer';
                        const cellValue = variant[col] !== undefined ? String(variant[col]) : '-';
                        
                        if (isArtikelnummer) {
                          return (
                            <td key={col} className="px-4 py-3 text-sm">
                              <Link 
                                to={`/produkter/${product.slug || product.id}?variant=${encodeURIComponent(cellValue)}`}
                                className="text-[#321a27] font-medium hover:underline"
                              >
                                {cellValue}
                              </Link>
                            </td>
                          );
                        }
                        
                        return (
                          <td key={col} className="px-4 py-3 text-sm text-stone-700">
                            {cellValue}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-right">
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addToQuote(product, variant, 1);
                            toast.success(language === 'sv' ? 'Tillagd i offertlistan!' : 'Added to quote list!');
                          }}
                          className="px-3 py-1 bg-[#321a27] text-white text-sm hover:bg-[#4a2d3d] inline-flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          {language === 'sv' ? 'Offertlista' : 'Quote'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Filter Dropdown Component (same as ProductsPage)
const FilterDropdown = ({ label, options, selected, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-2 text-sm border flex items-center gap-2 ${
          selected.length > 0 
            ? 'bg-[#f5f0f2] border-[#d4c4cc] text-[#321a27]' 
            : 'bg-white border-stone-300 text-stone-700 hover:border-stone-400'
        }`}
      >
        {label} {selected.length > 0 && `(${selected.length})`}
        <ChevronDown className="w-4 h-4" />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white border border-stone-200 shadow-lg z-20 min-w-[240px] max-h-64 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-3 text-xs text-stone-400 text-center">Inga tillgängliga</div>
            ) : (
              options.map(opt => (
                <label key={opt.value} className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-stone-50 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selected.includes(opt.value)}
                      onCheckedChange={() => onToggle(opt.value)}
                    />
                    <span className="text-sm text-stone-700">{opt.label}</span>
                  </div>
                  {typeof opt.count === 'number' && (
                    <span className="text-xs text-stone-400 font-mono">({opt.count})</span>
                  )}
                </label>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Filter Panel (slide-in from right)
const FilterPanel = ({
  language,
  dimensions,
  materials,
  pressureClasses,
  selectedDimensions,
  selectedMaterials,
  selectedPressureClasses,
  expandedFilters,
  setExpandedFilters,
  toggleFilter,
  clearFilters,
  onClose,
  resultCount
}) => {
  const toggleSection = (section) => {
    setExpandedFilters(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const FilterSection = ({ id, title, options, selected, type }) => (
    <div className="border-b border-stone-200">
      <button
        onClick={() => toggleSection(id)}
        className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-stone-50"
      >
        <span className="font-medium text-stone-900">
          {title}
          {selected.length > 0 && (
            <span className="ml-2 text-xs text-[#321a27] font-semibold">({selected.length})</span>
          )}
        </span>
        {expandedFilters[id] ? (
          <ChevronUp className="w-5 h-5 text-stone-500" />
        ) : (
          <span className="text-stone-400 text-xl">+</span>
        )}
      </button>
      {expandedFilters[id] && (
        <div className="px-4 pb-4 space-y-2 max-h-64 overflow-y-auto">
          {options.length === 0 ? (
            <p className="text-xs text-stone-400 py-2">
              {language === 'sv' ? 'Inga tillgängliga' : 'None available'}
            </p>
          ) : (
            options.map(opt => (
              <label key={opt.value} className="flex items-center justify-between gap-3 cursor-pointer">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selected.includes(opt.value)}
                    onCheckedChange={() => toggleFilter(type, opt.value)}
                  />
                  <span className="text-sm text-stone-700">{opt.label}</span>
                </div>
                {typeof opt.count === 'number' && (
                  <span className="text-xs text-stone-400 font-mono">({opt.count})</span>
                )}
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-4 py-4 border-b border-stone-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-stone-900">
            {language === 'sv' ? 'Filtrera' : 'Filter'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded">
            <X className="w-6 h-6 text-stone-600" />
          </button>
        </div>

        {/* Filter Sections */}
        <div className="flex-1 overflow-y-auto">
          <FilterSection
            id="dimension"
            title={language === 'sv' ? 'Dimension' : 'Dimension'}
            options={dimensions.map(d => ({ value: d.value, label: d.value, count: d.count }))}
            selected={selectedDimensions}
            type="dimension"
          />
          <FilterSection
            id="material"
            title={language === 'sv' ? 'Material' : 'Material'}
            options={materials.map(m => ({ value: m.value, label: m.value, count: m.count }))}
            selected={selectedMaterials}
            type="material"
          />
          <FilterSection
            id="pressureClass"
            title={language === 'sv' ? 'Tryckklass (PN)' : 'Pressure Class (PN)'}
            options={pressureClasses.map(p => ({ value: p.value, label: p.value, count: p.count }))}
            selected={selectedPressureClasses}
            type="pressureClass"
          />
        </div>

        {/* Footer Actions */}
        <div className="px-4 py-4 border-t border-stone-200 flex gap-4">
          <button
            onClick={clearFilters}
            className="flex-1 px-4 py-3 border border-stone-300 text-stone-700 hover:bg-stone-50 flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            {language === 'sv' ? 'Rensa alla filter' : 'Clear all filters'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-[#321a27] text-white hover:bg-[#4a2d3d]"
          >
            {language === 'sv' ? `Visa ${resultCount} produkter` : `Show ${resultCount} products`}
          </button>
        </div>
      </div>
    </>
  );
};

export default CategoryPage;

